import { useState } from 'react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { updateStaffInputSchema, type StaffDetail, type StaffRow, type UpdateStaffInput } from '@mybusinessmate/schema';
import { useUpdateStaff } from '../../lib/directory-api';
import { ROLE_OPTIONS } from '../../lib/staff-form';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Input } from '../ui/input';
import { NumberStepper } from '../ui/number-stepper';
import { SegmentedControl } from '../ui/segmented-control';
import { TagInput } from '../ui/tag-input';
import { useToast } from '../ui/toast';

type Person = Pick<StaffRow, 'id' | 'name' | 'staffRole' | 'title' | 'department' | 'skills' | 'maxActiveTasks'>;

function Form({
  person,
  onClose,
  onSaved,
}: {
  person: Person;
  onClose: () => void;
  onSaved?: (updated: StaffDetail) => void;
}) {
  const toast = useToast();
  const update = useUpdateStaff(person.id);
  const [values, setValues] = useState({
    staffRole: person.staffRole,
    title: person.title ?? '',
    department: person.department ?? '',
    skills: person.skills,
    maxActiveTasks: person.maxActiveTasks,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();

  const set = <K extends keyof typeof values>(key: K, value: (typeof values)[K]) =>
    setValues((v) => ({ ...v, [key]: value }));
  const roleHelp = ROLE_OPTIONS.find((r) => r.value === values.staffRole)?.help;

  const submit = async () => {
    setFormError(undefined);
    const body: UpdateStaffInput = {
      staffRole: values.staffRole,
      title: values.title.trim() || undefined,
      department: values.department.trim() || undefined,
      skills: values.skills,
      maxActiveTasks: values.maxActiveTasks,
    };
    // Same schema as the API, so the form can never disagree with the server about what is valid.
    const parsed = updateStaffInputSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path.join('.'), i.message])));
      return;
    }
    setErrors({});
    try {
      const updated = await update.mutateAsync(body);
      toast({ title: 'Saved', description: `${person.name}’s details were updated.` });
      onSaved?.(updated);
      onClose();
    } catch (err) {
      const e = err as Partial<MappedApiError>;
      if (e.kind === 'validation' && e.fieldErrors?.length)
        setErrors(Object.fromEntries(e.fieldErrors.map((f) => [f.field, f.message])));
      // The API explains why (the only admin, someone who still has clients), so show its words.
      else if (e.status === 409) setFormError(e.message ?? 'That change isn’t allowed right now.');
      else if (e.kind === 'forbidden') setFormError('Only admins can change the team.');
      else if (e.kind === 'not_found') setFormError('This person is no longer on the team.');
      else if (e.kind === 'unauthorized') setFormError('Your session expired. Please sign in again.');
      else setFormError('We couldn’t save the changes. Please try again.');
    }
  };

  return (
    <div className="flex flex-col gap-(--mbm-space-4)">
      {formError ? <Banner tone="error">{formError}</Banner> : null}
      <div className="flex flex-col gap-(--mbm-space-1-5)">
        <SegmentedControl
          label="Role"
          shape="compact"
          options={ROLE_OPTIONS}
          value={values.staffRole}
          onChange={(staffRole) => set('staffRole', staffRole)}
        />
        {errors.staffRole ? (
          <p role="alert" className="text-caption text-feedback-error">
            {errors.staffRole}
          </p>
        ) : roleHelp ? (
          <p className="text-caption text-text-tertiary">{roleHelp}</p>
        ) : null}
      </div>
      <div className="grid gap-(--mbm-space-3) sm:grid-cols-2">
        <Input
          label="Department"
          variant="compact"
          value={values.department}
          error={errors.department}
          onChange={(e) => set('department', e.target.value)}
        />
        <Input
          label="Title"
          variant="compact"
          value={values.title}
          error={errors.title}
          onChange={(e) => set('title', e.target.value)}
        />
      </div>
      <TagInput
        compact
        label="Skills"
        placeholder="Type a skill and press Enter"
        value={values.skills}
        onChange={(skills) => set('skills', skills)}
      />
      <NumberStepper
        compact
        label="Max active tasks"
        helperText="Dispatch warns above this."
        value={values.maxActiveTasks}
        onChange={(n) => set('maxActiveTasks', n)}
      />
      <div className="flex flex-col-reverse gap-(--mbm-space-2) sm:flex-row sm:justify-end">
        <Button variant="ghost" size="small" onClick={onClose} disabled={update.isPending}>
          Cancel
        </Button>
        <Button size="small" onClick={() => void submit()} loading={update.isPending}>
          Save changes
        </Button>
      </div>
    </div>
  );
}

/** Role and profile of someone on the team (Figma A04 row menu "Change role", profile "Edit"). Admin only; the API refuses unsafe changes. */
export function EditStaffDialog({
  person,
  onClose,
  onSaved,
}: {
  person: Person | null;
  onClose: () => void;
  onSaved?: (updated: StaffDetail) => void;
}) {
  return (
    <Dialog
      open={!!person}
      onClose={onClose}
      size="wide"
      density="form"
      title={person ? `Edit ${person.name}` : 'Edit'}
      description="Their email stays the same."
    >
      {person ? <Form key={person.id} person={person} onClose={onClose} onSaved={onSaved} /> : null}
    </Dialog>
  );
}
